import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { Box } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Faqs } from '@prisma/client';
import { ThemeIds } from '@porter/shared-theme';
import { BookingSiteSocials } from '../../../types/bookingSite';
import { CUSTOMER_WEB_URL } from '../../../constants/global';
import { CANVAS, LINE_MID, resetButton } from './constants';

const PREVIEW_ORIGIN = new URL(CUSTOMER_WEB_URL).origin;

/**
 * Logical widths the iframe renders at, then scaled to fit the pane — so
 * "Desktop" is a real desktop viewport, not the width left beside the settings.
 */
const WIDTHS = { desktop: 1280, mobile: 390 };

type Viewport = keyof typeof WIDTHS;

interface Props {
  url: string;
  /** Null while nothing is selected — the frame shows the real, unthemed page. */
  theme: ThemeIds | null;
  /** Why the page differs from the picks, e.g. a primary too faint for headings. */
  notices?: string[];
  /** Sent alongside the theme: they render on the page, so they preview too. */
  socials: BookingSiteSocials;
  gallery: string[] | null;
  faqs: Pick<Faqs, 'faqId' | 'header' | 'text'>[] | null;
  isDirty: boolean;
  /** Bumped when something saves outside the draft, e.g. the logo. */
  refreshToken: number;
}

function SitePreview({
  url,
  theme,
  notices = [],
  socials,
  gallery,
  faqs,
  isDirty,
  refreshToken,
}: Props) {
  const frameRef = useRef<HTMLIFrameElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const [viewport, setViewport] = useState<Viewport>('desktop');
  const [box, setBox] = useState({ width: 0, height: 0 });

  const src = `${url}${url.includes('?') ? '&' : '?'}preview=1`;

  useLayoutEffect(() => {
    const element = boxRef.current;
    if (!element) return undefined;
    const observer = new ResizeObserver(([entry]) =>
      setBox({
        width: entry.contentRect.width,
        height: entry.contentRect.height,
      }),
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const push = useCallback(() => {
    frameRef.current?.contentWindow?.postMessage(
      {
        type: 'porter:theme',
        // Null is posted, not skipped: after Discard the frame has to be told
        // to drop the theme. Socials ride along either way — not part of it.
        theme: theme && {
          backgroundId: theme.backgroundId,
          primaryId: theme.primaryId,
          accentId: theme.accentId,
          fontId: theme.fontId,
        },
        socials: {
          instagramHandle: socials.instagramHandle,
          tiktokHandle: socials.tiktokHandle,
          facebookUrl: socials.facebookUrl,
          websiteUrl: socials.websiteUrl,
        },
        gallery,
        faqs,
      },
      PREVIEW_ORIGIN,
    );
  }, [theme, socials, gallery, faqs]);

  // The frame may not be listening yet, so ready and load both push again.
  // Wrapped so a future return value is never mistaken for a cleanup.
  useEffect(() => {
    push();
  }, [push]);

  useEffect(() => {
    if (!refreshToken) return;
    frameRef.current?.contentWindow?.postMessage(
      { type: 'porter:refresh' },
      PREVIEW_ORIGIN,
    );
  }, [refreshToken]);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== PREVIEW_ORIGIN) return;
      if (event.data?.type !== 'porter:preview-ready') return;
      push();
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [push]);

  const logicalWidth = WIDTHS[viewport];
  const scale = box.width ? Math.min(1, box.width / logicalWidth) : 1;

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        background: CANVAS,
        display: 'flex',
        flexDirection: 'column',
      }}>
      <Box
        sx={{
          height: 56,
          flex: 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          padding: '0 24px',
        }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {(Object.keys(WIDTHS) as Viewport[]).map(key => (
            <Box
              key={key}
              component="button"
              type="button"
              onClick={() => setViewport(key)}
              aria-pressed={viewport === key}
              sx={{
                ...resetButton,
                padding: '7px 14px',
                borderRadius: '50px',
                background: viewport === key ? '#000' : 'transparent',
                color: viewport === key ? '#fff' : 'text.secondary',
                fontSize: 12,
                fontWeight: 700,
                textTransform: 'capitalize',
              }}>
              {key}
            </Box>
          ))}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <Box
            sx={{
              fontSize: 12,
              fontWeight: 600,
              color: 'text.tertiary',
              display: { xs: 'none', sm: 'block' },
            }}>
            {isDirty ? 'Preview · not published' : 'Preview'}
          </Box>
          <Box
            component="button"
            type="button"
            onClick={() => window.open(url, '_blank', 'noopener')}
            sx={{
              ...resetButton,
              fontSize: 14,
              fontWeight: 700,
              color: 'primary.main',
            }}>
            Open site ›
          </Box>
        </Box>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, padding: '0 24px 24px' }}>
        <Box
          sx={{
            height: '100%',
            border: '1px solid #000',
            borderRadius: '10px',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            background: '#fff',
          }}>
          <Box
            sx={{
              height: 34,
              flex: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '0 12px',
              background: CANVAS,
              borderBottom: `1px solid ${LINE_MID}`,
            }}>
            <Box sx={{ display: 'flex', gap: '5px' }}>
              {[0, 1, 2].map(dot => (
                <Box
                  key={dot}
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: LINE_MID,
                  }}
                />
              ))}
            </Box>
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                height: 18,
                borderRadius: '50px',
                background: '#fff',
                display: 'flex',
                alignItems: 'center',
                padding: '0 10px',
                fontSize: 10,
                color: 'text.tertiary',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}>
              {url.replace(/^https?:\/\//, '')}
            </Box>
          </Box>

          <Box
            ref={boxRef}
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: 'hidden',
              position: 'relative',
            }}>
            {notices.length > 0 && (
              <Box
                role="status"
                sx={{
                  position: 'absolute',
                  top: 12,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  zIndex: 1,
                  maxWidth: 'calc(100% - 32px)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '6px',
                  pointerEvents: 'none',
                }}>
                {notices.map(notice => (
                  <Box
                    key={notice}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '8px',
                      padding: '9px 14px',
                      borderRadius: '10px',
                      background: 'rgba(20, 20, 20, 0.88)',
                      color: '#fff',
                      fontSize: 12,
                      lineHeight: 1.4,
                      boxShadow: '0 4px 14px rgba(0, 0, 0, 0.18)',
                    }}>
                    <InfoOutlinedIcon
                      sx={{ fontSize: 15, flex: 'none', marginTop: '1px' }}
                    />
                    {notice}
                  </Box>
                ))}
              </Box>
            )}
            <Box
              sx={{
                width: logicalWidth * scale,
                height: '100%',
                margin: '0 auto',
                overflow: 'hidden',
              }}>
              <Box
                component="iframe"
                ref={frameRef}
                title="Booking site preview"
                src={src}
                onLoad={push}
                sx={{
                  display: 'block',
                  width: logicalWidth,
                  height: box.height ? box.height / scale : '100%',
                  border: 'none',
                  background: '#fff',
                  transform: `scale(${scale})`,
                  transformOrigin: 'top left',
                }}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default SitePreview;
